import { jsPDF } from 'jspdf';
import ExcelJS from 'exceljs';
import { ReportData } from '../ReportGeneratorTypes';
import { getCharterersText, getFormattedDate, getLetterForNumber } from '../excelReportUtils';

/**
 * Generates a PDF report header with the Odfjell logo
 * @param doc - jsPDF document instance
 * @param data - data object containing vessel and port call information
 */
export function generateReportHeaderPdf(doc: jsPDF, data: ReportData, headerTitle: string, orientation: string): void {
    doc.setFontSize(11);
    doc.addImage('/assets/logos/odfjell_logo.png', 'PNG', 13, 5, 18, 13);

    doc.text(data.vessel.vesselName, 35, 10);
    doc.text(headerTitle, 35, 16);
    if (data.selectedCharterers && data.selectedCharterers.length > 0) {
        const charterersText = getCharterersText(data.selectedCharterers);
        doc.text(charterersText, 120, 10);
    }
    const today = new Date();
    const formattedDate = getFormattedDate(today);
    doc.setFontSize(9);
    doc.text(`Created ${formattedDate}`, orientation === 'portrait' ? 180 : 250, 17);

    doc.setLineWidth(0.3);
    doc.line(10, 20, orientation === 'portrait' ? 210 : 287, 20);
}

/**
 * Generates the Excel report header (logo, vessel name, title, charterers, "Created" date, and
 * the separator border under it).
 * @param endColumn - 0-indexed column that the header (border line, charterers, "Created" date)
 *   should extend to. Callers should pass the table's actual last column so the header always
 *   spans exactly as wide as the table below it.
 * @param titleEndColumn - 0-indexed column that the vessel name/title box extends to (defaults to
 *   6, i.e. column G, matching the original fixed C:G box). The "Created" date is placed
 *   immediately after this column and right-aligned, so it hugs the top-right corner of
 *   `endColumn` regardless of how wide the vessel name/title box itself is.
 */
export function generateReportHeaderExcel(
    worksheet: ExcelJS.Worksheet,
    vesselName: string,
    title: string,
    charterers: string[],
    logoImageId: number,
    endColumn: number = 12,
    titleEndColumn: number = 6
): void {
    worksheet.mergeCells('A1:B5');
    worksheet.addImage(logoImageId, 'A1:B5');

    const titleEndLetter = getLetterForNumber(titleEndColumn);
    worksheet.mergeCells(`C2:${titleEndLetter}2`);
    worksheet.getCell('C2').value = vesselName;
    worksheet.getCell('C2').font = { size: 16, bold: true };
    worksheet.mergeCells(`C4:${titleEndLetter}4`);
    worksheet.getCell('C4').value = title;
    worksheet.getCell('C4').font = { size: 16, bold: true };

    if (charterers && charterers.length > 0) {
        const charterersStartColumn = Math.min(titleEndColumn + 2, endColumn);
        const charterersStartLetter = getLetterForNumber(charterersStartColumn);
        worksheet.mergeCells(`${charterersStartLetter}4:${getLetterForNumber(endColumn)}4`);
        const charterersText = getCharterersText(charterers);
        worksheet.getCell(`${charterersStartLetter}4`).value = charterersText;
    }

    const today = new Date();
    const formattedDate = getFormattedDate(today);
    const createdStartColumn = Math.min(titleEndColumn + 1, endColumn);
    const createdStartLetter = getLetterForNumber(createdStartColumn);
    worksheet.mergeCells(`${createdStartLetter}2:${getLetterForNumber(endColumn)}2`);
    const createdCell = worksheet.getCell(`${createdStartLetter}2`);
    createdCell.value = 'Created ' + formattedDate;
    createdCell.font = { size: 12 };
    createdCell.alignment = { horizontal: 'right' };

    worksheet.mergeCells(`A6:${getLetterForNumber(endColumn)}6`);
    worksheet.getCell('A6').border = {
        bottom: { style: 'medium' },
    };
    worksheet.getRow(6).height = 2;
}
